import { BarChart3, BookOpenCheck, Home, NotebookPen, Settings, UsersRound } from 'lucide-react';
import { lazy, Suspense, useLayoutEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';

import { BrandMark } from './components/brand-mark';
import { useDeviceFeatures } from './hooks/use-device-features';
import { useOnline } from './hooks/use-online';
import { SeoMetadata } from './seo-metadata';

const InAppReminder = lazy(async () => ({
  default: (await import('./features/reminders/in-app-reminder')).InAppReminder,
}));

const tabs = [
  { to: '/', label: '오늘', icon: Home, end: true },
  { to: '/logs', label: '교재', icon: NotebookPen },
  { to: '/notes', label: '오답', icon: BookOpenCheck },
  { to: '/report', label: '리포트', icon: BarChart3 },
] as const;

export function AppShell() {
  const online = useOnline();
  const deviceFeatures = useDeviceFeatures();
  const { pathname, search, hash } = useLocation();
  useLayoutEffect(() => {
    if (hash) {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView();
    } else window.scrollTo(0, 0);
  }, [pathname, search, hash]);
  const isOnboarding = pathname === '/onboarding';

  return (
    <div className={isOnboarding ? 'pn-app-shell pn-app-shell--onboarding' : 'pn-app-shell'}>
      <SeoMetadata />
      <a className="pn-skip-link" href="#main-content">
        본문으로 바로가기
      </a>
      <header className="pn-topbar">
        <NavLink aria-label="회독루프 홈" className="pn-topbar__brand" to="/">
          <BrandMark />
        </NavLink>
        <div className="pn-topbar__actions">
          {deviceFeatures ? (
            <NavLink aria-label="같이 공부하기" className="pn-icon-link" to="/rooms">
              <UsersRound aria-hidden size={21} />
            </NavLink>
          ) : null}
          <NavLink aria-label="설정" className="pn-icon-link" to="/settings">
            <Settings aria-hidden size={21} />
          </NavLink>
        </div>
      </header>

      <div className="pn-banner-stack">
        {online ? null : (
          <p className="pn-offline-banner" role="status">
            오프라인이에요. 기록은 이 기기에 계속 저장할 수 있어요.
          </p>
        )}

        <Suspense fallback={null}>
          <InAppReminder />
        </Suspense>
      </div>

      <main className="pn-main" id="main-content">
        <Outlet />
      </main>

      <footer className="pn-trust-footer">
        <Link to="/privacy">개인정보</Link>
        <Link to="/terms">이용 안내</Link>
        <Link to="/support">지원</Link>
        <Link to="/help">도움말</Link>
      </footer>

      {isOnboarding ? null : (
        <nav aria-label="주요 메뉴" className="pn-tabbar">
          {tabs.map(({ to, label, icon: Icon, ...linkProps }) => (
            <NavLink
              className={({ isActive }) => (isActive ? 'pn-tab is-active' : 'pn-tab')}
              key={to}
              to={to}
              {...linkProps}
            >
              <Icon aria-hidden size={22} strokeWidth={1.9} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
